import { useCallback, useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@shadcn/ui/components/ui/card';
import { Slider } from '@shadcn/ui/components/ui/slider';
import { Label } from '@shadcn/ui/components/ui/label';
import { Button } from '@shadcn/ui/components/ui/button';
import { Separator } from '@shadcn/ui/components/ui/separator';
import {
    UserObjectives,
    UserTemporalWeights,
    WeightKeys,
    TemporalWeightKeys,
    DEFAULT_OBJECTIVE_WEIGHTS,
    DEFAULT_TEMPORAL_WEIGHTS,
} from '@andsend/intelligence-shared';
import { X } from 'lucide-react';
import { debounce } from '../../../../helpers/debounce';

interface WeightSlidersProps {
    weights: UserObjectives;
    temporalWeights: UserTemporalWeights;
    onWeightsChange: (weights: UserObjectives) => void;
    onTemporalWeightsChange: (weights: UserTemporalWeights) => void;
    onClose: () => void;
}

const WEIGHT_LABELS: Record<WeightKeys, string> = {
    networking: 'Networking',
    sales: 'Sales',
    customerSuccess: 'Customer Success',
    fundraising: 'Fundraising',
    recruiting: 'Recruiting',
    internal: 'Internal',
};

const TEMPORAL_WEIGHT_LABELS: Record<TemporalWeightKeys, string> = {
    timing_urgency: 'AI Urgency Boost (0-50%)',
    urgent_followup_boost: 'Urgent Follow-up Boost (0-300%)',
    no_action_penalty: 'No Action Penalty',
    shelf_base_boost: 'Shelf Base Boost',
    shelf_period_days: 'Shelf Period (days)',
    very_old_overdue_base_boost: 'Very Old Penalty',
    very_old_decay_rate: 'Very Old Decay Rate',
    due_today_boost: 'Due Today Boost',
    due_curve_width: 'Due Curve Width',
    future_penalty: 'Future Penalty',
    future_decay_rate: 'Future Decay Rate',
};

const TEMPORAL_WEIGHT_RANGES: Record<TemporalWeightKeys, { min: number; max: number; step: number }> = {
    timing_urgency: { min: 0, max: 0.5, step: 0.01 },
    urgent_followup_boost: { min: 0, max: 3, step: 0.1 },
    no_action_penalty: { min: 0, max: 1, step: 0.01 },
    shelf_base_boost: { min: 0, max: 3, step: 0.1 },
    shelf_period_days: { min: 1, max: 30, step: 1 },
    very_old_overdue_base_boost: { min: 0, max: 2, step: 0.1 },
    very_old_decay_rate: { min: 0.5, max: 10, step: 0.1 },
    due_today_boost: { min: 0, max: 3, step: 0.1 },
    due_curve_width: { min: 0.1, max: 5, step: 0.1 },
    future_penalty: { min: 0, max: 5, step: 0.1 },
    future_decay_rate: { min: 0.1, max: 10, step: 0.1 },
};

export const WeightSliders = ({
    weights,
    temporalWeights,
    onWeightsChange,
    onTemporalWeightsChange,
    onClose,
}: WeightSlidersProps) => {
    // Local state for immediate UI updates
    const [localWeights, setLocalWeights] = useState<UserObjectives>(weights);
    const [localTemporalWeights, setLocalTemporalWeights] = useState<UserTemporalWeights>(temporalWeights);

    // Sync local state when external weights change
    useEffect(() => {
        setLocalWeights(weights);
    }, [weights]);

    useEffect(() => {
        setLocalTemporalWeights(temporalWeights);
    }, [temporalWeights]);

    // Debounced functions to call the external callbacks
    const debouncedOnWeightsChange = useMemo(
        () =>
            debounce((newWeights: UserObjectives) => {
                onWeightsChange(newWeights);
            }, 200),
        [onWeightsChange]
    );

    const debouncedOnTemporalWeightsChange = useMemo(
        () =>
            debounce((newWeights: UserTemporalWeights) => {
                onTemporalWeightsChange(newWeights);
            }, 200),
        [onTemporalWeightsChange]
    );

    const handleSliderChange = useCallback(
        (key: WeightKeys, value: number[]) => {
            const newWeights = {
                ...localWeights,
                [key]: value[0],
            };

            // Update local state immediately for smooth UI
            setLocalWeights(newWeights);

            // Debounce the external callback
            debouncedOnWeightsChange(newWeights);
        },
        [localWeights, debouncedOnWeightsChange]
    );

    const handleTemporalSliderChange = useCallback(
        (key: TemporalWeightKeys, value: number[]) => {
            const newWeights = {
                ...localTemporalWeights,
                [key]: value[0],
            };

            // Update local state immediately for smooth UI
            setLocalTemporalWeights(newWeights);

            // Debounce the external callback
            debouncedOnTemporalWeightsChange(newWeights);
        },
        [localTemporalWeights, debouncedOnTemporalWeightsChange]
    );

    const resetToDefaults = useCallback(() => {
        setLocalWeights(DEFAULT_OBJECTIVE_WEIGHTS);
        setLocalTemporalWeights(DEFAULT_TEMPORAL_WEIGHTS);
        onWeightsChange(DEFAULT_OBJECTIVE_WEIGHTS);
        onTemporalWeightsChange(DEFAULT_TEMPORAL_WEIGHTS);
    }, [onWeightsChange, onTemporalWeightsChange]);

    return (
        <Card className="w-80 bg-background shadow-lg border max-h-[80vh] overflow-y-auto">
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium">Custom Weights</CardTitle>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onClose}>
                        <X className="h-4 w-4" />
                    </Button>
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Objective Weights Section */}
                <div className="space-y-3">
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        Content Priorities
                    </h4>
                    {(Object.keys(WEIGHT_LABELS) as WeightKeys[]).map((key) => (
                        <div key={key} className="space-y-2">
                            <div className="flex items-center justify-between">
                                <Label htmlFor={key} className="text-xs font-medium">
                                    {WEIGHT_LABELS[key]}
                                </Label>
                                <span className="text-xs text-muted-foreground">
                                    {(localWeights[key] || 1).toFixed(1)}
                                </span>
                            </div>
                            <Slider
                                id={key}
                                min={0}
                                max={5}
                                step={0.1}
                                value={[localWeights[key] || 1]}
                                onValueChange={(value) => handleSliderChange(key, value)}
                                className="w-full"
                            />
                        </div>
                    ))}
                </div>

                <Separator />

                {/* Temporal Weights Section */}
                <div className="space-y-3">
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        Timing Behavior
                    </h4>
                    {(Object.keys(TEMPORAL_WEIGHT_LABELS) as TemporalWeightKeys[]).map((key) => {
                        const range = TEMPORAL_WEIGHT_RANGES[key];
                        const currentValue = localTemporalWeights[key] ?? DEFAULT_TEMPORAL_WEIGHTS[key];

                        return (
                            <div key={key} className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <Label htmlFor={key} className="text-xs font-medium">
                                        {TEMPORAL_WEIGHT_LABELS[key]}
                                    </Label>
                                    <span className="text-xs text-muted-foreground">
                                        {range.step >= 1 ? currentValue.toFixed(0) : currentValue.toFixed(2)}
                                    </span>
                                </div>
                                <Slider
                                    id={key}
                                    min={range.min}
                                    max={range.max}
                                    step={range.step}
                                    value={[currentValue]}
                                    onValueChange={(value) => handleTemporalSliderChange(key, value)}
                                    className="w-full"
                                />
                            </div>
                        );
                    })}
                </div>

                <Separator />

                <div className="pt-2">
                    <Button variant="outline" size="sm" onClick={resetToDefaults} className="w-full text-xs">
                        Reset All to Defaults
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
};
